import React from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, GeoJSON, Popup, CircleMarker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { OSM_TILE_URL, OSM_ATTRIBUTION } from '../map/mapUtils';
import { ExternalLink } from 'lucide-react';

export const LUXEMBOURG_CENTER = [49.95, 5.5];
export const STATUS_COLORS = {
  'PERMIS URBANISATION': '#F59E0B',
  PERMIS: '#1E3A8A',
  ACCORD: '#6B7280',
};

export const fmtArea = (m2) => {
  if (!m2) return '—';
  return m2 >= 10000 ? `${(m2 / 10000).toFixed(2)} ha` : `${Math.round(m2).toLocaleString('fr-BE')} m²`;
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-BE') : '—');

const colorFor = (z) => STATUS_COLORS[z.status] || '#10B981';

export const zoneToJobLink = (z) => {
  const p = new URLSearchParams({
    city: z.commune || '', country: 'Belgium', lat: z.latitude || '', lng: z.longitude || '',
    title: `Lotissement à ${z.commune} – ${z.status_label || 'permis d\'urbanisation'}`,
    description: `Nouveau lotissement autorisé à ${z.commune} (${z.province})${z.area_m2 ? `, superficie ${fmtArea(z.area_m2)}` : ''}${z.decision_date ? `, décision du ${new Date(z.decision_date).toLocaleDateString('fr-BE')}` : ''}. Réf. SPW ${z.code}.`,
  });
  return `/dashboard/post-job?${p.toString()}`;
};

export const ZonePopup = ({ zone, access, canPost }) => (
  <div className="text-sm min-w-[200px]" data-testid={`zone-popup-${zone.code}`}>
    <p className="font-heading font-bold text-[#111827]">{zone.commune}</p>
    <p className="text-xs text-[#6B7280]">{zone.province}</p>
    <p className="mt-1"><span className="inline-block w-2 h-2 rounded-full mr-1" style={{ background: colorFor(zone) }} />{zone.status_label}</p>
    {access ? (
      <div className="mt-2 space-y-0.5 text-xs text-[#374151]">
        <p>Superficie : <strong>{fmtArea(zone.area_m2)}</strong></p>
        <p>Date d'autorisation : <strong>{fmtDate(zone.decision_date)}</strong></p>
        {zone.decision_type && <p>Décision : {zone.decision_type}</p>}
        <p className="text-[#9CA3AF]">Réf. {zone.code}</p>
        {zone.document_url && (
          <a href={zone.document_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#1E3A8A] font-medium mt-1">
            <ExternalLink className="w-3 h-3" /> Document officiel
          </a>
        )}
        {canPost && (
          <Link to={zoneToJobLink(zone)} className="block mt-2 text-center bg-[#F59E0B] text-white rounded-md px-2 py-1.5 font-semibold" data-testid={`zone-post-job-${zone.code}`}>
            Publier un chantier ici
          </Link>
        )}
      </div>
    ) : (
      <p className="mt-2 text-xs text-[#9CA3AF] italic">Superficie, date d'autorisation et document : réservés aux abonnés Premium.</p>
    )}
  </div>
);

export const ZonesMap = ({ zones, access, canPost = false, center = LUXEMBOURG_CENTER, zoom = 9 }) => (
  <MapContainer center={center} zoom={zoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true} data-testid="zones-map">
    <TileLayer attribution={OSM_ATTRIBUTION} url={OSM_TILE_URL} />
    {zones.filter((z) => z.latitude && z.longitude).map((z) => (
      <CircleMarker key={`c-${z.code}-${access}`} center={[z.latitude, z.longitude]} radius={9} pathOptions={{ color: 'white', weight: 2, fillColor: colorFor(z), fillOpacity: 0.9 }}>
        <Popup><ZonePopup zone={z} access={access} canPost={canPost} /></Popup>
      </CircleMarker>
    ))}
    {zones.filter((z) => z.geometry).map((z) => (
      <GeoJSON
        key={`${z.code}-${access}`}
        data={z.geometry}
        style={{ color: colorFor(z), weight: 2, fillColor: colorFor(z), fillOpacity: 0.35 }}
      >
        <Popup><ZonePopup zone={z} access={access} canPost={canPost} /></Popup>
      </GeoJSON>
    ))}
  </MapContainer>
);
